import usePermissions from "@/hooks/usePermissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldAlert } from "lucide-react";

interface RequireMenuAccessProps {
  /** Sidebar menu `id` whose access rules gate this route (e.g. "admin", "billing"). */
  menuId: string;
  children: React.ReactNode;
}

/**
 * Route-level access guard. Renders its children only when the current user may
 * access the given sidebar menu `id` (per `usePermissions().canAccessMenu`).
 * Otherwise it renders a clear access-denied notice instead of the page.
 *
 * This is the route-enforcement counterpart to the sidebar's `canAccessMenu`
 * visibility check: hiding a menu item is not enough on its own because the
 * route can still be deep-linked. Both share the single source of truth in
 * `usePermissions`, so the sidebar and the routes can never drift.
 *
 * A denied state is rendered (not a silent redirect) so the behaviour is
 * testable and the user gets explicit feedback.
 */
export default function RequireMenuAccess({
  menuId,
  children,
}: RequireMenuAccessProps) {
  const { canAccessMenu } = usePermissions();

  if (!canAccessMenu(menuId)) {
    return (
      <div className="flex-1 p-6" data-testid="route-access-denied">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access restricted</AlertTitle>
          <AlertDescription>
            You do not have permission to access this page. It is limited to
            AthlynAdmin users.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
